import React, { useState } from 'react';
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TableSortLabel, CircularProgress, Box, Chip, Tooltip, Typography } from '@mui/material';
import { TabPanel} from "@mui/lab";
import { SearchOutlined, WarningAmberOutlined } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useNavigate } from 'react-router-dom';
import useSortableTable from "../utils/useSortableTable";

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const ProjectNameCell = styled(TableCell)(({ theme }) => ({
  cursor: 'pointer',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '200px',
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));

const LoadingBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8),
}));

const MoneyCell = styled(TableCell)({
  whiteSpace: 'nowrap',
});

const ProfitCell = styled(MoneyCell, { shouldForwardProp: (prop) => prop !== 'isNegative' })(({ theme, isNegative }) => ({
  color: isNegative ? theme.palette.error.main : 'inherit',
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const CellWithFlag = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '4px',
});

const UnderstatedIcon = styled(WarningAmberOutlined)(({ theme }) => ({
  fontSize: '1rem',
  color: theme.palette.warning.main,
}));

const fmt = (v) => `$ ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hours = (v) => `${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Hrs`;
const fixedPriceHours = (p) => (p.time?.total?.["Fixed Price"] || 0) / 60;
const marginOf = (quoted, cost) => (quoted > 0 ? ((quoted - cost) / quoted) * 100 : null);

export const isCostKnown = (row) => row?.type !== 'Group';

const UNKNOWN_COST_NOTE = "Time is logged against this group's projects, never against the group itself, so its hours, labor cost, profit and margin cannot be measured here";
const UNDERSTATED_COST_NOTE = 'Some hours on this row were logged by someone with no hourly wage on file, so the labor cost is lower — and the profit and margin higher — than the work really was';

const NotAvailable = ({ title }) => (
  <Tooltip title={title}>
    <Chip label="N/A" size="small" />
  </Tooltip>
);

const ProjectProfitabilityTable = ({ data, loading, hasFetched }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const navigate = useNavigate();

  const { sorted, order, handleSort } = useSortableTable(data, {
    name: p => p.name,
    type: p => p.type,
    client: p => p.client,
    status: p => p.status,
    estimatedTime: p => p.estimatedTime || 0,
    quotedTime: p => p.quotedTime || 0,
    actualHours: p => (isCostKnown(p) ? fixedPriceHours(p) : null),
    quotedPrice: p => p.quotedPrice || 0,
    laborCost: p => (isCostKnown(p) ? p.actualCost || 0 : null),
    profit: p => (isCostKnown(p) ? (p.quotedPrice || 0) - (p.actualCost || 0) : null),
    margin: p => (isCostKnown(p) ? marginOf(p.quotedPrice || 0, p.actualCost || 0) : null),
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleProjectClick = (accountSlug, projectId, type) => {
    if (type === 'Group') {
      navigate(`/account/${accountSlug}`);
    } else {
      navigate(`/account/${accountSlug}/project/${projectId}`);
    }
  };

  return (
    <StyledTabPanel value="profitability">
      <Paper>
        <TableContainer>
          {loading ? (
            <LoadingBox>
              <CircularProgress />
            </LoadingBox>
          ) : (
            <Table stickyHeader>
              <TableHead>
                <TableRow key="header">
                  <TableCell sortDirection={order.key === 'name' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'name'} direction={order.key === 'name' ? order.dir : 'asc'} onClick={() => handleSort('name')}>Project / Group</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'type' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'type'} direction={order.key === 'type' ? order.dir : 'asc'} onClick={() => handleSort('type')}>Type</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'client' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'client'} direction={order.key === 'client' ? order.dir : 'asc'} onClick={() => handleSort('client')}>Client</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={order.key === 'status' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'status'} direction={order.key === 'status' ? order.dir : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'estimatedTime' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'estimatedTime'} direction={order.key === 'estimatedTime' ? order.dir : 'asc'} onClick={() => handleSort('estimatedTime')}>Estimated Hours</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'quotedTime' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'quotedTime'} direction={order.key === 'quotedTime' ? order.dir : 'asc'} onClick={() => handleSort('quotedTime')}>Quoted Hours</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'actualHours' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'actualHours'} direction={order.key === 'actualHours' ? order.dir : 'asc'} onClick={() => handleSort('actualHours')}>Actual Hours</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'quotedPrice' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'quotedPrice'} direction={order.key === 'quotedPrice' ? order.dir : 'asc'} onClick={() => handleSort('quotedPrice')}>Quoted Price</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'laborCost' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'laborCost'} direction={order.key === 'laborCost' ? order.dir : 'asc'} onClick={() => handleSort('laborCost')}>Labor Cost</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'profit' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'profit'} direction={order.key === 'profit' ? order.dir : 'asc'} onClick={() => handleSort('profit')}>Profit</TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={order.key === 'margin' ? order.dir : false}>
                    <TableSortLabel active={order.key === 'margin'} direction={order.key === 'margin' ? order.dir : 'asc'} onClick={() => handleSort('margin')}>Margin %</TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((project) => {
                  const quoted = project?.quotedPrice || 0;
                  const cost = project?.actualCost || 0;
                  const costKnown = isCostKnown(project);
                  const understated = costKnown && !!project.costUnderstated;
                  const profit = quoted - cost;
                  const margin = marginOf(quoted, cost);
                  return (
                    <TableRow key={project._id} hover>
                      <Tooltip title={project.name}>
                        <ProjectNameCell onClick={() => handleProjectClick(project.accountSlug, project._id, project.type)}>{project.name}</ProjectNameCell>
                      </Tooltip>
                      <TableCell>
                        <Chip label={project.type === 'Group' ? 'Group' : 'Project'} size="small" variant="outlined" color={project.type === 'Group' ? 'secondary' : 'default'} />
                      </TableCell>
                      <TableCell>{project.client}</TableCell>
                      <TableCell>
                        <Chip
                          label={project.status}
                          size="small"
                          color={project.status === "Completed" || project.status === "Approved" ? "success" : project.status === "In Progress" || project.status === "Testing" || project.status === "Ready for Review" ? "primary" : project.status === "On Hold" || project.status === "Awaiting Authorization" ? "warning" : project.status === "Cancelled" ? "error" : "default"}
                        />
                      </TableCell>
                      <TableCell align="right">{hours(project.estimatedTime)}</TableCell>
                      <TableCell align="right">{hours(project.quotedTime)}</TableCell>
                      <TableCell align="right">
                        {costKnown ? hours(fixedPriceHours(project)) : <NotAvailable title={UNKNOWN_COST_NOTE} />}
                      </TableCell>
                      <MoneyCell align="right">{fmt(quoted)}</MoneyCell>
                      <MoneyCell align="right">
                        {!costKnown ? (
                          <NotAvailable title={UNKNOWN_COST_NOTE} />
                        ) : understated ? (
                          <Tooltip title={UNDERSTATED_COST_NOTE}>
                            <CellWithFlag>
                              {fmt(cost)}
                              <UnderstatedIcon />
                            </CellWithFlag>
                          </Tooltip>
                        ) : (
                          fmt(cost)
                        )}
                      </MoneyCell>
                      {costKnown ? (
                        <ProfitCell align="right" isNegative={profit < 0}>{fmt(profit)}</ProfitCell>
                      ) : (
                        <MoneyCell align="right"><NotAvailable title={UNKNOWN_COST_NOTE} /></MoneyCell>
                      )}
                      <TableCell align="right">
                        {!costKnown ? (
                          <NotAvailable title={UNKNOWN_COST_NOTE} />
                        ) : margin === null ? (
                          <NotAvailable title="No quoted price on this row, so there is no revenue to measure a margin against" />
                        ) : understated ? (
                          <Tooltip title={UNDERSTATED_COST_NOTE}>
                            <CellWithFlag>
                              <Chip label={`${margin.toFixed(1)}%`} size="small" color={margin >= 30 ? "success" : margin >= 15 ? "primary" : margin >= 0 ? "warning" : "error"} />
                              <UnderstatedIcon />
                            </CellWithFlag>
                          </Tooltip>
                        ) : (
                          <Chip label={`${margin.toFixed(1)}%`} size="small" color={margin >= 30 ? "success" : margin >= 15 ? "primary" : margin >= 0 ? "warning" : "error"} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <EmptyStateBox>
                        <SearchOutlined />
                        <Typography variant="body1">{hasFetched ? 'No projects match the current filters' : 'Click Generate Report to load data'}</Typography>
                      </EmptyStateBox>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!loading && data.length > 25 && <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={data.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />}
      </Paper>
    </StyledTabPanel>
  );
};

export default ProjectProfitabilityTable;